/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  createSilentRetryOptions as createModelSilentRetryOptions,
  SILENT_MAX_RETRY_AFTER_MS,
  SILENT_RETRY_DELAYS_MS
} from "../../../model/retrymechanism/silentRetryOptions";
import type { HttpMethod, TRetryOptions } from "../../../model/types";
import { isDefinitiveSignInFailure } from "./retryOverlay";

export { SILENT_MAX_RETRY_AFTER_MS, SILENT_RETRY_DELAYS_MS };

/**
 * Retry options for a step of a **silent** sign-in flow (IP auto-login).
 *
 * A silent flow runs with the user looking at the splash screen and has an
 * alternative to waiting: the next auth type. So, unlike
 * `createCriticalRetryOptions`, it:
 *
 * - raises **no overlay** and takes no manual "retry now" signal — there is
 *   nothing on screen for the user to act on;
 * - **gives up**: INTERACTIVE with a short warm-up, about 9s per step, after
 *   which the caller falls back;
 * - ends at once on a definitive rejection (the same
 *   `isDefinitiveSignInFailure` rule the blocking flows use), **including** an
 *   account-suspension 403 — the next auth type will meet the same account and
 *   can wait on the server's schedule behind its own overlay;
 * - ends at once when the server asks for a wait longer than
 *   {@link SILENT_MAX_RETRY_AFTER_MS}.
 *
 * The ladder and the `Retry-After` limit are the model's
 * (`model/retrymechanism/silentRetryOptions`), shared with the background
 * token refresh; this adds the sign-in terminality rule.
 *
 * @param httpMethod - The call's method. INTERACTIVE only retries a 502 / 504
 *   for an idempotent method, so the DTS GETs pass "GET".
 */
export function createSilentRetryOptions(
  httpMethod?: HttpMethod
): TRetryOptions {
  return createModelSilentRetryOptions(httpMethod, isDefinitiveSignInFailure);
}
